import { useConsultationStateInitializer } from '@/state-management/slices/consultations/hooks/useConsultationStateInitializer';
import type { ConsultationPricingTier, PaginationCursors, ResponseCursors } from '@/types';
import type { PaginationModel } from '@montugroup/design-system';
import type { TelecomWaitingRoomEntryStatus } from '@montugroup/pms-api-contracts';
import type { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetPatientConsultations } from './useGetPatientConsultations';

export interface Consultation {
  id: number;
  consultation_code: string;
  end_time: string;
  start_time: string;
  doctor_id?: number | undefined;
  payment_status: string | null;
  paid_date?: string | null;
  consult_type: string;
  notes?: string | null | undefined;
  zoom_url?: string | null;
  status_id?: number;
  sub_status_id?: number;
  payment_queued?: boolean;
  calcom_consultation_uuid?: string | null;
  xero_consultation_code?: string | null;
  payment_id?: string | null;
  ConsultationStatus?: ConsultationStatus | null;
  ConsultationSubStatus?: ConsultationSubStatus | null;
  ConsultationType: ConsultationType;
  Doctor?: Doctor | null;
  Nurse?: Nurse | null;
  pricingTier: ConsultationPricingTier | null;
  waiting_room_status?: TelecomWaitingRoomEntryStatus | null;
  isMarkedAsComplimentary?: boolean | null;
  hasPaidInitialNurseConsult?: boolean | null;
}

interface ConsultationStatus {
  id: number;
  name: string;
}

interface ConsultationSubStatus {
  id: number;
  name: string;
}

interface ConsultationType {
  type?: string | null;
  duration_mins?: number;
}

interface Doctor {
  id: number;
  prescriber_number?: string | null;
  doctor_code?: string | null;
  calcom_initial_consult_event?: string | null;
  calcom_follow_up_consult_event?: string | null;
  DoctorUser: DoctorUser;
}

interface DoctorUser {
  first_name?: string | null;
  last_name?: string | null;
  id?: number;
}

interface Nurse {
  id: number;
  NurseUser: NurseUser;
}

interface NurseUser {
  first_name?: string | null;
  last_name?: string | null;
}

export const usePatientConsultations = (patientId?: number) => {
  const [consultationsCursors, setConsultationsCursors] = useState<PaginationCursors>({ prev: null, next: null });

  const [consultationsSearch, setConsultationsSearch] = useState<string>('');

  const [consultationsResponseCursors, setConsultationsResponseCursors] = useState<ResponseCursors>();

  const [sort, setSort] = useState<string>('-startTime');

  const pageParams = useMemo(
    () => ({
      size: 5,
      after: consultationsCursors?.next ?? undefined,
      before: consultationsCursors?.prev ?? undefined,
    }),
    [consultationsCursors],
  );

  const handlePaginationChange = ({ page: isNewPage }: PaginationModel) => {
    const cursorKey = isNewPage > 0 ? 'next' : 'prev';
    const newCursor = consultationsResponseCursors?.[cursorKey];

    setConsultationsCursors((prevCursors) => {
      if (!newCursor || prevCursors?.[cursorKey] === newCursor) {
        return prevCursors; // Prevents unnecessary state updates
      }

      return cursorKey === 'next' ? { prev: null, next: newCursor } : { prev: newCursor, next: null };
    });
  };

  const onSort = useCallback((sort: { id: string; desc: boolean }[]): void => {
    const direction = sort?.[0]?.desc === false ? 'asc' : 'desc';
    setConsultationsCursors({ prev: null, next: null });
    setSort(direction === 'desc' ? '-startTime' : 'startTime');
  }, []);

  const {
    data: patientConsultations,
    isFetching: isFetchingPatientConsultations,
    refetch: refetchConsultations,
    isError: isPatientConsultationsError,
    isSuccess: isPatientConsultationsSuccess,
    isPending: isPatientConsultationsPending,
  } = useGetPatientConsultations({ pageParams, patientId, search: consultationsSearch, sort });

  const patientConsultationsData: Consultation[] = useMemo(() => {
    if (!patientConsultations || !isPatientConsultationsSuccess) {
      return [];
    }

    const consultations = patientConsultations?.data?.map((consultation) => ({
      id: consultation.id,
      consultation_code: consultation.attributes.code,
      end_time: consultation.attributes.endTime,
      start_time: consultation.attributes.startTime,
      doctor_id: consultation.attributes.doctor?.id,
      payment_status: consultation.attributes.paymentStatus,
      paid_date: consultation.attributes.paidDate,
      consult_type: consultation.attributes.type,
      notes: consultation.attributes.notes,
      zoom_url: consultation.attributes.zoomUrl,
      status_id: consultation.attributes.consultationStatus?.id,
      sub_status_id: consultation.attributes.consultationSubStatus?.id,
      payment_queued: consultation.attributes.paymentQueued,
      calcom_consultation_uuid: consultation.attributes.calcomConsultationUuid,
      xero_consultation_code: consultation.attributes.xeroConsultationCode,
      payment_id: consultation.attributes.paymentId,
      ConsultationStatus: consultation.attributes.consultationStatus,
      ConsultationSubStatus: consultation.attributes.consultationSubStatus,
      pricingTier: consultation.attributes.pricingTier
        ? {
            id: consultation.attributes.pricingTier.id,
            name: consultation.attributes.pricingTier.name as PaymentRequestType,
          }
        : null,
      isMarkedAsComplimentary: consultation.attributes.isMarkedAsComplimentary ?? null,
      ConsultationType: {
        type: consultation.attributes.consultationType?.type,
        duration_mins: consultation.attributes.consultationType?.durationMinutes,
      },
      Doctor: consultation.attributes.doctor
        ? {
            id: consultation.attributes.doctor?.id,
            prescriber_number: consultation.attributes.doctor.prescriberNumber,
            doctor_code: consultation.attributes.doctor.doctorCode,
            calcom_initial_consult_event: consultation.attributes.doctor.calcomInitialConsultEvent,
            calcom_follow_up_consult_event: consultation.attributes.doctor.calcomFollowUpConsultEvent,
            DoctorUser: {
              first_name: consultation.attributes.doctor?.firstName,
              last_name: consultation.attributes.doctor?.lastName,
              id: consultation.attributes.doctor?.userId,
            },
          }
        : null,
      Nurse: consultation.attributes.nurse
        ? {
            id: consultation.attributes.nurse?.id,
            NurseUser: {
              first_name: consultation.attributes.nurse?.firstName,
              last_name: consultation.attributes.nurse?.lastName,
            },
          }
        : null,
      waiting_room_status: consultation.attributes.waitingRoomStatus,
      hasPaidInitialNurseConsult: consultation.attributes?.hasPaidInitialNurseConsult ?? null,
    }));

    return consultations || [];
  }, [patientConsultations, isPatientConsultationsSuccess]);

  useConsultationStateInitializer({
    consultations: patientConsultationsData,
    isSuccess: isPatientConsultationsSuccess,
  });

  useEffect(() => {
    if (patientConsultations && isPatientConsultationsSuccess) {
      setConsultationsResponseCursors(patientConsultations.meta?.page?.cursor);
    }
  }, [patientConsultations, isPatientConsultationsSuccess]);

  return {
    isFetchingPatientConsultations,
    patientConsultationsData,
    consultationsResponseCursors,
    refetchConsultations,
    handlePaginationChange,
    setConsultationsCursors,
    setConsultationsSearch,
    onSort,
    isPatientConsultationsError,
    isPatientConsultationsSuccess,
    isPatientConsultationsPending,
  };
};
